import { useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { ClavePermiso, Yo } from '@libreta/contratos';
import { avisarSesionPerdida, ErrorDeApi, guardarToken, pedir, renovarSesion } from '../api/cliente';

type Estado = { tipo: 'cargando' } | { tipo: 'afuera' } | { tipo: 'adentro'; yo: Yo };

interface Sesion {
  estado: Estado;
  ingresar: (usuario: string, clave: string, entidadId?: string) => Promise<void>;
  salir: () => Promise<void>;
  cambiarEntidad: (entidadId: string) => Promise<void>;
  volverAPlataforma: () => Promise<void>;
  recargar: () => Promise<void>;
}

const Contexto = createContext<Sesion | null>(null);

/**
 * La marca de la entidad se inyecta en runtime sobre el token base: un solo build sirve a
 * todas las entidades, en claro y en oscuro.
 */
function aplicarMarca(color: string | null | undefined): void {
  if (color) document.documentElement.style.setProperty('--marca-base', color);
  else document.documentElement.style.removeProperty('--marca-base');
}

export function ProveedorSesion({ children }: { children: ReactNode }) {
  const [estado, setEstado] = useState<Estado>({ tipo: 'cargando' });
  const consultas = useQueryClient();

  const cargarYo = useCallback(async () => {
    const yo = await pedir<Yo>('/auth/yo');
    aplicarMarca(yo.entidad?.colorMarca);
    document.title = yo.entidad ? `${yo.entidad.nombre} · Libreta` : 'Libreta';
    setEstado({ tipo: 'adentro', yo });
  }, []);

  useEffect(() => {
    avisarSesionPerdida(() => {
      guardarToken(null);
      consultas.clear();
      setEstado({ tipo: 'afuera' });
    });
    // Al recargar la página el token en memoria se perdió: se intenta renovar con la cookie.
    renovarSesion()
      .then((token) => (token ? cargarYo() : setEstado({ tipo: 'afuera' })))
      .catch(() => setEstado({ tipo: 'afuera' }));
  }, [cargarYo, consultas]);

  const conTokens = useCallback(
    async (promesa: Promise<{ accessToken: string }>) => {
      const { accessToken } = await promesa;
      guardarToken(accessToken);
      consultas.clear();
      await cargarYo();
    },
    [cargarYo, consultas],
  );

  const valor = useMemo<Sesion>(
    () => ({
      estado,
      ingresar: (usuario, clave, entidadId) =>
        conTokens(pedir('/auth/login', { metodo: 'POST', cuerpo: { usuario, clave, entidadId } })),
      salir: async () => {
        await pedir('/auth/logout', { metodo: 'POST' }).catch(() => undefined);
        guardarToken(null);
        consultas.clear();
        aplicarMarca(null);
        setEstado({ tipo: 'afuera' });
      },
      cambiarEntidad: (entidadId) => conTokens(pedir('/auth/entidad', { metodo: 'POST', cuerpo: { entidadId } })),
      volverAPlataforma: () => conTokens(pedir('/auth/plataforma', { metodo: 'POST' })),
      recargar: cargarYo,
    }),
    [estado, conTokens, cargarYo, consultas],
  );

  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

export function useSesion(): Sesion {
  const sesion = useContext(Contexto);
  if (!sesion) throw new Error('useSesion fuera de ProveedorSesion');
  return sesion;
}

/** Para las pantallas de adentro: el usuario ya está garantizado. */
export function useYo(): Yo {
  const { estado } = useSesion();
  if (estado.tipo !== 'adentro') throw new Error('useYo sin sesión');
  return estado.yo;
}

/** Ocultar un botón es UX; la API igual verifica el permiso por su cuenta. */
export function usePuede(): (permiso: ClavePermiso) => boolean {
  const yo = useYo();
  return useCallback((permiso) => yo.permisos.includes(permiso), [yo.permisos]);
}

export function useMoneda(): string {
  return useYo().entidad?.moneda ?? 'PYG';
}

export function esElegirEntidad(error: unknown): error is ErrorDeApi & { detalles: { entidades: Array<{ id: string; nombre: string; rol: string }> } } {
  return error instanceof ErrorDeApi && error.codigo === 'ELEGIR_ENTIDAD';
}
